import type {
  MIFederatedSourceOptions, MIFederatedQueryParams, MIFederatedQueryResult,
} from './types';
import { MIServerRegistry } from './MIServerRegistry';
import { MIOGCClient } from './MIOGCClient';

export class MIFederatedSource extends EventTarget {
  private options: MIFederatedSourceOptions;
  private sources: { serverId: string; layerName: string; type: string }[];

  constructor(options: MIFederatedSourceOptions) {
    super();
    this.options = {
      mergeStrategy: 'union',
      deduplication: false,
      maxConcurrent: 5,
      timeout: 30000,
      ...options,
    };
    this.sources = [...options.sources];
  }

  addSource(source: { serverId: string; layerName: string; type: string }): void {
    this.sources.push(source);
  }

  removeSource(serverId: string, layerName: string): void {
    this.sources = this.sources.filter(
      s => !(s.serverId === serverId && s.layerName === layerName)
    );
  }

  async query(params: MIFederatedQueryParams): Promise<MIFederatedQueryResult> {
    const startTime = Date.now();
    const registry = this.options.registry as MIServerRegistry;
    const allFeatures: any[] = [];
    const sourceResults: MIFederatedQueryResult['sourceResults'] = [];

    const batches: Promise<void>[] = [];
    const maxConcurrent = this.options.maxConcurrent || 5;

    for (let i = 0; i < this.sources.length; i += maxConcurrent) {
      const batch = this.sources.slice(i, i + maxConcurrent);
      const batchPromises = batch.map(async (source) => {
        const sourceStart = Date.now();
        try {
          const server = registry.get(source.serverId);
          if (!server) {
            sourceResults.push({
              serverId: source.serverId,
              layerName: source.layerName,
              featureCount: 0,
              durationMs: Date.now() - sourceStart,
              error: 'Server not registered',
            });
            return;
          }

          let features: any[] = [];

          if (source.type === 'wfs' || source.type === 'ogc-features') {
            const client = new MIOGCClient({
              url: server.url,
              auth: server.auth,
              proxy: server.proxy,
              timeout: this.options.timeout,
            });

            const result = await client.getFeature({
              typeName: source.layerName,
              bbox: params.bbox,
              maxFeatures: params.maxFeatures,
              outputFormat: 'application/json',
            });

            features = result.features || [];
          } else if (source.type === 'geojson') {
            const url = `${server.url}/${source.layerName}`;
            const response = await fetch(url);
            const data = await response.json();
            features = data.features || [];
          }

          if (params.bbox && features.length > 0) {
            features = features.filter((f: any) => {
              if (!f.geometry?.coordinates) return true;
              return true;
            });
          }

          features.forEach((f: any) => {
            f.properties = f.properties || {};
            f.properties._sourceServer = source.serverId;
            f.properties._sourceLayer = source.layerName;
          });

          allFeatures.push(...features);
          sourceResults.push({
            serverId: source.serverId,
            layerName: source.layerName,
            featureCount: features.length,
            durationMs: Date.now() - sourceStart,
          });
        } catch (err: any) {
          sourceResults.push({
            serverId: source.serverId,
            layerName: source.layerName,
            featureCount: 0,
            durationMs: Date.now() - sourceStart,
            error: err.message || 'Query failed',
          });
        }
      });

      await Promise.allSettled(batchPromises);
    }

    let resultFeatures = allFeatures;
    if (this.options.deduplication && this.options.deduplicationKey) {
      const seen = new Set<string>();
      resultFeatures = allFeatures.filter(f => {
        const key = f.properties?.[this.options.deduplicationKey!];
        if (key && seen.has(String(key))) return false;
        if (key) seen.add(String(key));
        return true;
      });
    }

    return {
      features: resultFeatures,
      sourceResults,
      totalFeatures: resultFeatures.length,
      queryDurationMs: Date.now() - startTime,
    };
  }

  getSourceInfo(): { serverId: string; layerName: string; featureCount: number; status: string }[] {
    return this.sources.map(s => ({
      serverId: s.serverId,
      layerName: s.layerName,
      featureCount: 0,
      status: 'registered',
    }));
  }
}
